import { TimelineEvent } from '../../types/invitation';
import { supabase } from './client';
import { createLogger } from '../logger';

const timelineLog = createLogger('timeline');

export const timelineService = {
  async getTimelineEvents(invitationId: string): Promise<TimelineEvent[]> {
    timelineLog.debug('查詢時間軸事件', invitationId);

    const { data, error } = await supabase
      .from('timeline_events')
      .select('*')
      .eq('invitation_id', invitationId)
      .order('display_order', { ascending: true })
      .order('target_date', { ascending: true });

    if (error) {
      timelineLog.error('查詢時間軸事件錯誤:', error);
      throw error;
    }

    timelineLog.info('成功取得', data.length, '個事件');
    return data as TimelineEvent[];
  },

  async addTimelineEvent(
    eventData: Omit<TimelineEvent, 'id' | 'created_at' | 'updated_at' | 'user_id'>
  ): Promise<TimelineEvent> {
    timelineLog.debug('新增時間軸事件', eventData);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('必須登入才能新增時間軸事件');
    }

    if (!eventData.invitation_id) {
      throw new Error('缺少 invitation_id');
    }
    if (!eventData.title || eventData.title.trim() === '') {
      throw new Error('任務標題不能為空');
    }
    if (!eventData.target_date) {
      throw new Error('目標日期不能為空');
    }

    const dataToInsert = {
      ...eventData,
      user_id: user.id,
      title: eventData.title.trim(),
      description: eventData.description?.trim() || null,
      category: eventData.category?.trim() || null
    };
    timelineLog.debug('準備插入的資料', dataToInsert);

    const { data, error } = await supabase
      .from('timeline_events')
      .insert([dataToInsert])
      .select()
      .single();

    if (error) {
      timelineLog.error('新增時間軸事件錯誤:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw new Error(`新增時間軸事件失敗: ${error.message} (${error.code})`);
    }

    timelineLog.info('成功新增事件', data.id);
    return data as TimelineEvent;
  },

  async updateTimelineEvent(
    eventId: string,
    updates: Partial<Omit<TimelineEvent, 'id' | 'created_at' | 'updated_at' | 'user_id' | 'invitation_id'>>
  ): Promise<TimelineEvent> {
    timelineLog.debug('更新時間軸事件', eventId, updates);

    const { data, error } = await supabase
      .from('timeline_events')
      .update(updates)
      .eq('id', eventId)
      .select()
      .single();

    if (error) {
      timelineLog.error('更新時間軸事件錯誤:', error);
      throw error;
    }

    timelineLog.info('成功更新事件', eventId);
    return data as TimelineEvent;
  },

  async deleteTimelineEvent(eventId: string): Promise<void> {
    timelineLog.debug('刪除時間軸事件', eventId);

    const { error } = await supabase
      .from('timeline_events')
      .delete()
      .eq('id', eventId);

    if (error) {
      timelineLog.error('刪除時間軸事件錯誤:', error);
      throw error;
    }

    timelineLog.info('成功刪除事件', eventId);
  },

  async updateTaskCompletion(eventId: string, isCompleted: boolean): Promise<void> {
    timelineLog.debug('更新任務完成狀態', eventId, isCompleted);

    const { error } = await supabase
      .from('timeline_events')
      .update({ is_completed: isCompleted })
      .eq('id', eventId);

    if (error) {
      timelineLog.error('更新任務完成狀態錯誤:', error);
      throw error;
    }

    timelineLog.info('成功更新完成狀態', eventId, '→', isCompleted);
  },

  async updateEventOrder(eventUpdates: Array<{ id: string; display_order: number }>): Promise<void> {
    timelineLog.debug('批量更新事件順序', eventUpdates.length, '個事件');

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('必須登入才能更新事件順序');
    }

    const updates = eventUpdates.map(({ id, display_order }) => ({
      id,
      display_order,
      user_id: user.id
    }));

    const { error } = await supabase
      .from('timeline_events')
      .upsert(updates, { onConflict: 'id' });

    if (error) {
      timelineLog.error('批量更新事件順序錯誤:', error);
      throw error;
    }

    timelineLog.info('成功更新事件順序');
  }
};
